import { useEffect, useRef } from 'react';
import { Loader2, RefreshCw, AlertCircle, Bot, Building2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { AiWorkingIndicator } from '@/components/common/AiWorkingIndicator';
import { useToast } from '@/components/ui/use-toast';
import { useRegenerateAiContent, useSetAiContentOverride } from '@/api/synced-locations';
import { aiContentJobLabel, formatAiRunUsage } from '@/lib/ai-run-usage';
import type { SyncedLocation } from '@/types/api';

interface AiContentPanelProps {
  location: SyncedLocation;
}

/**
 * Displays AI-generated and partner-provided content side by side for
 * a synced location. Renders for all partners.
 *
 * Provides a "Regenerate" button that enqueues a fresh AI content generation job.
 * Generation runs in a queue worker for seconds (minutes behind a batch) after that request
 * returns, so the panel's working state follows the job the server reports
 * (`aiContent.job`, polled by `useSyncedLocation`), not the request: queued, generating,
 * retrying, then a toast when the result or the failure lands. Under the result it shows what
 * the run cost (`aiContent.run`).
 */
export function AiContentPanel({ location }: AiContentPanelProps): JSX.Element {
  const regenerateMutation = useRegenerateAiContent();
  const overrideMutation = useSetAiContentOverride();
  const { toast } = useToast();

  const { aiContent } = location;
  // When the server could not read the job, keep the one last seen: an unknown state must not
  // read as "finished" (no false toast, no re-enabled button while the job may still run).
  const lastKnownJob = useRef(aiContent?.job ?? null);
  if (!aiContent?.jobLookupFailed) {
    lastKnownJob.current = aiContent?.job ?? null;
  }
  const job = lastKnownJob.current;
  // Starting = the enqueue request (and the refetch after it) is in flight; then the job takes over.
  const isStarting = regenerateMutation.isPending && job === null;
  const isWorking = isStarting || job !== null;
  const run = aiContent?.run ?? null;

  // Say how the job ended when it leaves the queue while the page is open.
  const previousJob = useRef(job);
  useEffect(() => {
    const hadJob = previousJob.current !== null;
    previousJob.current = job;
    if (!hadJob || job !== null) return;
    if (aiContent?.lastError) {
      toast({ variant: 'destructive', title: 'AI generation failed', description: aiContent.lastError });
    } else if (aiContent?.generatedAt) {
      toast({ title: 'AI title and description ready' });
    }
  }, [job, aiContent?.lastError, aiContent?.generatedAt, toast]);

  const handleRegenerate = (): void => {
    regenerateMutation.mutate(location.id, {
      onError: (error) => {
        toast({
          variant: 'destructive',
          title: 'Could not start AI generation',
          description: error instanceof Error ? error.message : 'Try again in a moment.',
        });
      },
    });
  };

  // Toggle is ON when partner data is being used (useAiContent === false on server)
  const toggleChecked = aiContent?.useAiContent === false;

  const handleToggleChange = (checked: boolean): void => {
    overrideMutation.mutate({ id: location.id, useAiContent: !checked });
  };

  const generatedAt = aiContent?.generatedAt
    ? formatDistanceToNow(new Date(aiContent.generatedAt), { addSuffix: true })
    : null;

  const failedAt = aiContent?.lastErrorAt
    ? formatDistanceToNow(new Date(aiContent.lastErrorAt), { addSuffix: true })
    : null;

  return (
    <Card data-testid="ai-content-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bot className="h-5 w-5" />
            AI-generated content
          </CardTitle>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="outline"
              onClick={handleRegenerate}
              disabled={isWorking}
              data-testid="ai-content-regenerate-btn"
            >
              {isWorking ? (
                <Loader2 className="mr-2 h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {isWorking ? 'Generating…' : 'Regenerate'}
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Switch
            checked={toggleChecked}
            onCheckedChange={handleToggleChange}
            disabled={overrideMutation.isPending}
            data-testid="ai-content-use-partner-toggle"
            id="ai-content-use-partner-toggle"
          />
          <label
            htmlFor="ai-content-use-partner-toggle"
            className="text-sm text-muted-foreground cursor-pointer select-none"
          >
            Use partner data
          </label>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          When ON, the marketplace shows partner-supplied title and description for this listing instead of AI-generated content. AI content remains stored and can be re-enabled at any time.
        </p>
      </CardHeader>
      <CardContent>
        {isWorking && (
          <AiWorkingIndicator
            className="mb-4 rounded-md border bg-muted/40 p-3"
            label={job ? aiContentJobLabel(job) : 'Starting…'}
            since={job?.enqueuedAt ?? null}
            // While a retry waits, the stored error is the reason the last attempt failed.
            detail={job?.state === 'retrying' && aiContent?.lastError ? `Last error: ${aiContent.lastError}` : null}
            data-testid="ai-content-working"
          />
        )}

        {!isWorking && aiContent?.lastError && (
          <div
            className="mb-4 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
            data-testid="ai-content-error"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">{aiContent.lastError}</p>
              {failedAt && (
                <p className="mt-0.5 text-xs text-destructive/80">Failed {failedAt}</p>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6">
          {/* Left column: AI content */}
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
              <Bot className="h-3.5 w-3.5" />
              AI content
            </div>

            {isWorking && !location.aiTitle ? (
              <div className="space-y-2" aria-busy="true" data-testid="ai-content-skeleton">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : (
              // While a job replaces existing content, keep it readable, dimmed as outgoing.
              <div
                className={isWorking ? 'space-y-3 opacity-60' : 'space-y-3'}
                aria-busy={isWorking || undefined}
                data-testid="ai-content-current"
              >
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Title</p>
                  <p
                    className="text-sm font-medium"
                    data-testid="ai-content-ai-title"
                  >
                    {location.aiTitle ?? <span className="text-muted-foreground italic">Not generated</span>}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Description</p>
                  <p
                    className="text-sm whitespace-pre-wrap"
                    data-testid="ai-content-ai-description"
                  >
                    {location.aiDescription ?? <span className="text-muted-foreground italic">Not generated</span>}
                  </p>
                </div>
                <div className="space-y-0.5 pt-1 border-t">
                  {generatedAt && (
                    <p
                      className="text-xs text-muted-foreground"
                      data-testid="ai-content-generated-at"
                    >
                      Generated {generatedAt}
                    </p>
                  )}
                  {aiContent?.model && (
                    <p
                      className="text-xs text-muted-foreground"
                      data-testid="ai-content-model"
                    >
                      Model: {aiContent.model}
                    </p>
                  )}
                  {run && (
                    <p
                      className="text-xs text-muted-foreground"
                      // The run that wrote this content; spend on failed runs is only in the router ledger.
                      title={`Cost of AI Router run ${run.runId}, the run that wrote this content`}
                      data-testid="ai-content-usage"
                    >
                      {formatAiRunUsage(run)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right column: Partner content */}
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
              <Building2 className="h-3.5 w-3.5" />
              Partner content
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-1">Title</p>
              <p
                className="text-sm font-medium"
                data-testid="ai-content-partner-title"
              >
                {location.partnerTitle ?? <span className="text-muted-foreground italic">Not provided</span>}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Description</p>
              <p
                className="text-sm whitespace-pre-wrap"
                data-testid="ai-content-partner-description"
              >
                {location.partnerDescription ?? <span className="text-muted-foreground italic">Not provided</span>}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
